import { useCallback, useMemo, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { notAvailable } from './util/models';
import { isFeatureAvailable, featureNotAvailableError } from './util/feature-check';
import { CapacitorSQLite, SQLiteDBConnection, SQLiteConnection } from '@capacitor-community/sqlite';
export { SQLiteDBConnection };
/**
 * useSQLite Hook
 */
export const useSQLite = ({ onProgressImport, onProgressExport }) => {
    const platform = Capacitor.getPlatform();
    const sqlitePlugin = CapacitorSQLite;
    const mSQLite = useMemo(() => {
        return new SQLiteConnection(sqlitePlugin);
    }, [sqlitePlugin]);
    useEffect(() => {
        // init Listeners
        let importListener = null;
        let exportListener = null;
        if (onProgressImport && sqlitePlugin)
            importListener =
                sqlitePlugin.addListener('sqliteImportProgressEvent', (e) => {
                    onProgressImport(e.progress);
                });
        if (onProgressExport && sqlitePlugin)
            exportListener =
                sqlitePlugin.addListener('sqliteExportProgressEvent', (e) => {
                    onProgressExport(e.progress);
                });
        return () => {
            if (importListener)
                importListener.remove();
            if (exportListener)
                exportListener.remove();
        };
    }, []);
    const availableFeaturesN = {
        useSQLite: isFeatureAvailable('CapacitorSQLite', 'useSQLite')
    };
    const echo = useCallback(async (value) => {
        if (value) {
            const r = await mSQLite.echo(value);
            if (r) {
                return r;
            }
            else {
                return { value: null };
            }
        }
        else {
            return { value: null };
        }
    }, [mSQLite]);
    const getPlatform = useCallback(async () => {
        return { platform: platform };
    }, [platform]);
    /**
     * Create a Connection to Database
     * @param dbName string
     * @param _encrypted boolean optional
     * @param _mode string optional
     * @param version number optional
     */
    const createConnection = useCallback(async (dbName, encrypted, mode, version) => {
        if (dbName == null || dbName.length === 0) {
            return Promise.reject(new Error('Must provide a database name'));
        }
        const mDatabase = dbName;
        const mVersion = version ? version : 1;
        const mEncrypted = encrypted ? encrypted : false;
        const mMode = mode ? mode : "no-encryption";
        try {
            const r = await mSQLite.createConnection(mDatabase, mEncrypted, mMode, mVersion);
            if (r) {
                return Promise.resolve(r);
            }
            else {
                return Promise.reject("No returned connection");
            }
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    /**
     * Close the Connection to the Database
     * @param dbName string
     */
    const closeConnection = useCallback(async (dbName) => {
        if (dbName.length > 0) {
            try {
                await mSQLite.closeConnection(dbName);
                return Promise.resolve();
            }
            catch (err) {
                return Promise.reject(err);
            }
        }
        else {
            return Promise.reject('Must provide a database name');
        }
    }, [mSQLite]);
    /**
     * Check if database connection exists
     * @param database
     */
    const isConnection = useCallback(async (dbName) => {
        if (dbName.length > 0) {
            try {
                const r = await mSQLite.isConnection(dbName);
                if (r) {
                    return Promise.resolve(r);
                }
                else {
                    return Promise.reject("No returned isConnection");
                }
            }
            catch (err) {
                return Promise.reject(err);
            }
        }
        else {
            return Promise.reject('Must provide a database name');
        }
    }, [mSQLite]);
    /**
     * Check if database exists
     * @param database
     */
    const isDatabase = useCallback(async (dbName) => {
        if (dbName.length > 0) {
            try {
                const r = await mSQLite.isDatabase(dbName);
                if (r) {
                    return Promise.resolve(r);
                }
                else {
                    return Promise.reject("Error in isDatabase");
                }
            }
            catch (err) {
                return Promise.reject(err);
            }
        }
        else {
            return Promise.reject('Must provide a database name');
        }
    }, [mSQLite]);
    /**
     * Get the database list
     * @returns Promise<capSQLiteValues>
     * @since 1.0.1 refactor
     */
    const getDatabaseList = useCallback(async () => {
        try {
            const r = await mSQLite.getDatabaseList();
            if (r) {
                return Promise.resolve(r);
            }
            else {
                return Promise.reject("Error in getDatabaseList");
            }
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    /**
     * Add SQLIte Suffix to existing databases
     * @param folderPath
     */
    const addSQLiteSuffix = useCallback(async (folderPath) => {
        const path = folderPath ? folderPath : "default";
        try {
            await mSQLite.addSQLiteSuffix(path);
            return Promise.resolve();
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    /**
     * Delete Old Cordova databases
     * @param folderPath
     */
    const deleteOldDatabases = useCallback(async (folderPath) => {
        const path = folderPath ? folderPath : "default";
        try {
            await mSQLite.deleteOldDatabases(path);
            return Promise.resolve();
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    /**
     * Retrieve a Connection to the Database
     * @param dbName string
     */
    const retrieveConnection = useCallback(async (dbName) => {
        if (dbName.length > 0) {
            try {
                const r = await mSQLite.retrieveConnection(dbName);
                if (r) {
                    return Promise.resolve(r);
                }
                else {
                    return Promise.reject("No returned connection");
                }
            }
            catch (err) {
                return Promise.reject(err);
            }
        }
        else {
            return Promise.reject('Must provide a database name');
        }
    }, [mSQLite]);
    /**
     * Retrieve all Connections to Databases
     *
     */
    const retrieveAllConnections = useCallback(async () => {
        try {
            const r = await mSQLite.retrieveAllConnections();
            if (r) {
                return Promise.resolve(r);
            }
            else {
                return Promise.reject("No returned connection");
            }
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    /**
     * Close All Connections to Databases
     * @param dbName string
     */
    const closeAllConnections = useCallback(async () => {
        try {
            await mSQLite.closeAllConnections();
            return Promise.resolve();
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    /**
     * Import from Json
     * @param jsonstring string
     */
    const importFromJson = useCallback(async (jsonstring) => {
        try {
            const r = await mSQLite.importFromJson(jsonstring);
            if (r) {
                return Promise.resolve(r);
            }
            else {
                return Promise.reject('Error in importFromJson');
            }
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    /**
     * Is Json Valid
     * @param jsonstring string
     */
    const isJsonValid = useCallback(async (jsonstring) => {
        try {
            const r = await mSQLite.isJsonValid(jsonstring);
            if (r) {
                return Promise.resolve(r);
            }
            else {
                return Promise.reject('Error Json Object not valid');
            }
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    /**
     * Add the upgrade Statement for database version upgrading
     * @param dbName string
     * @param upgrade VersionUpgrade
     */
    const addUpgradeStatement = useCallback(async (dbName, upgrade) => {
        if (upgrade === null) {
            return Promise.reject(new Error("Must provide an upgrade statement"));
        }
        if (upgrade.fromVersion === null || upgrade.toVersion === null
            || upgrade.statement === null) {
            let msg = "Must provide an upgrade statement with ";
            msg += "fromVersion & toVersion & statement";
            return Promise.reject(msg);
        }
        if (dbName.length > 0) {
            try {
                await mSQLite
                    .addUpgradeStatement(dbName, upgrade.fromVersion, upgrade.toVersion, upgrade.statement, upgrade.set);
                return Promise.resolve();
            }
            catch (err) {
                return Promise.reject(err);
            }
        }
        else {
            return Promise.reject('Must provide a database name');
        }
    }, [mSQLite]);
    /**
     * Copy databases from assets to application database folder
     */
    const copyFromAssets = useCallback(async () => {
        const r = await mSQLite.copyFromAssets();
        try {
            await mSQLite.copyFromAssets();
            return Promise.resolve();
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    /**
     * Check the consistency between Js Connections
     * and Native Connections
     * if inconsistency all connections are removed
     */
    const checkConnectionsConsistency = useCallback(async () => {
        try {
            const r = await mSQLite.checkConnectionsConsistency();
            if (r) {
                return Promise.resolve(r);
            }
            else {
                return Promise.reject('Error Json Object not valid');
            }
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    const isSecretStored = useCallback(async () => {
        try {
            const r = await mSQLite.isSecretStored();
            if (r) {
                return Promise.resolve(r);
            }
            else {
                return Promise.reject('Error in isSecretStored');
            }
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    const setEncryptionSecret = useCallback(async (passphrase) => {
        if (passphrase == null || passphrase.length === 0) {
            return Promise.reject(new Error('Must provide a passphrase'));
        }
        try {
            await mSQLite.setEncryptionSecret(passphrase);
            return Promise.resolve();
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    const changeEncryptionSecret = useCallback(async (passphrase, oldpassphrase) => {
        if (passphrase == null || passphrase.length === 0) {
            return Promise.reject(new Error('Must provide a passphrase'));
        }
        if (oldpassphrase == null || oldpassphrase.length === 0) {
            return Promise.reject(new Error('Must provide the old passphrase'));
        }
        try {
            await mSQLite.changeEncryptionSecret(passphrase, oldpassphrase);
            return Promise.resolve();
        }
        catch (err) {
            return Promise.reject(err);
        }
    }, [mSQLite]);
    if (!availableFeaturesN.useSQLite) {
        return Object.assign({ echo: featureNotAvailableError, getPlatform: featureNotAvailableError, createConnection: featureNotAvailableError, closeConnection: featureNotAvailableError, retrieveConnection: featureNotAvailableError, retrieveAllConnections: featureNotAvailableError, closeAllConnections: featureNotAvailableError, addUpgradeStatement: featureNotAvailableError, importFromJson: featureNotAvailableError, isJsonValid: featureNotAvailableError, copyFromAssets: featureNotAvailableError, isConnection: featureNotAvailableError, isDatabase: featureNotAvailableError, getDatabaseList: featureNotAvailableError, addSQLiteSuffix: featureNotAvailableError, deleteOldDatabases: featureNotAvailableError, checkConnectionsConsistency: featureNotAvailableError, isSecretStored: featureNotAvailableError, setEncryptionSecret: featureNotAvailableError, changeEncryptionSecret: featureNotAvailableError }, notAvailable);
    }
    else {
        return { echo, getPlatform, createConnection, closeConnection,
            retrieveConnection, retrieveAllConnections, closeAllConnections,
            addUpgradeStatement, importFromJson, isJsonValid, copyFromAssets,
            isConnection, isDatabase, getDatabaseList, addSQLiteSuffix,
            deleteOldDatabases, checkConnectionsConsistency,
            isSecretStored, setEncryptionSecret, changeEncryptionSecret,
            isAvailable: true };
    }
};
//# sourceMappingURL=useSQLite.js.map